import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useSiteContent } from "@/lib/siteContent";

const EASE = [0.22, 1, 0.36, 1];

function Counter({ value, suffix, decimals }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      setDisplay((value * eased).toFixed(decimals));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, decimals]);

  return (
    <span ref={ref}>
      {display}
      <span className="text-[#3AB0E1]">{suffix}</span>
    </span>
  );
}

export default function Stats() {
  const content = useSiteContent();
  const STATS = content.stats;
  return (
    <section
      data-testid="stats-section"
      className="relative py-24 lg:py-32 bg-[#F5F8FC] border-y border-[#263562]/10 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(58,176,225,0.08),transparent_60%)]"
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#263562]/60 mb-14"
        >
          Regional Trust, Measured
        </motion.p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
              className="border-l border-[#263562]/15 pl-6"
            >
              <div className="font-display font-extrabold tracking-tight text-[clamp(1.75rem,3.4vw,2.6rem)] whitespace-nowrap text-[#263562]">
                <Counter value={s.value} suffix={s.suffix} decimals={s.decimals} />
              </div>
              <p className="mt-3 text-sm text-[#5A6B85] font-light">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
